'use client';
import Icon from '@/components/common/Icon';
import { menuItems } from '@/components/layout/menuConfig';
import { useStateContext } from '@/context/StateContext';
import { Avatar, Dropdown, Tooltip } from 'antd';
import { signOut } from 'next-auth/react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './AccountSidebar.module.scss';

/**
 * Memoized Logo Component
 * Prevents re-rendering when hover state changes in parent
 */
const SidebarLogo = memo(({ isCollapsed }) => (
  <div className={styles.logoSection}>
    <Link href="/account/dashboard">
      {isCollapsed ? (
        <Image src="/images/bm-symbol-white.svg" alt="Brandymail" width={32} height={32} priority />
      ) : (
        <Image
          src="/images/bm-logo-white.svg"
          alt="Brandymail Logo"
          width={195}
          height={65}
          priority
        />
      )}
    </Link>
  </div>
));

SidebarLogo.displayName = 'SidebarLogo';

/**
 * AccountSidebar Component
 * Left vertical navigation for account area
 * Replaces horizontal Header for /account/* pages
 *
 * @param {Object} session - NextAuth session object
 * @param {boolean} isMobile - Mobile device detection
 * @param {boolean} mobileMenuOpen - Mobile menu open state
 * @param {Function} setMobileMenuOpen - Mobile menu state setter
 * @param {boolean} defaultCollapsed - Override initial collapsed state (page-level config)
 */
const AccountSidebar = ({
  session,
  isMobile,
  mobileMenuOpen,
  setMobileMenuOpen,
  defaultCollapsed,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { showCart, setShowCart, totalQuantities } = useStateContext();

  // Mobile submenu state (for tap to expand)
  const [mobileOpenSubmenus, setMobileOpenSubmenus] = useState([]);

  // Touch gesture state (Phase 9.3: Swipe to dismiss)
  const [touchStart, setTouchStart] = useState(null);
  const [touchCurrent, setTouchCurrent] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const sheetRef = useRef(null);

  // Collapsed state (persisted in localStorage)
  // Page-level config (defaultCollapsed) overrides localStorage for initial mount only
  const [isCollapsed, setIsCollapsed] = useState(() =>
    defaultCollapsed !== undefined ? defaultCollapsed : false
  );

  // Sync with localStorage after hydration to avoid SSR/CSR mismatches
  const hasHydratedRef = useRef(false);
  const lastDefaultCollapsedRef = useRef(defaultCollapsed);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!hasHydratedRef.current) {
      hasHydratedRef.current = true;

      if (defaultCollapsed === undefined) {
        const storedValue = localStorage.getItem('sidebar_collapsed');

        if (storedValue !== null) {
          const shouldCollapse = storedValue === 'true';

          if (shouldCollapse !== isCollapsed) {
            setIsCollapsed(shouldCollapse);
            return;
          }
        }
      }
    }

    localStorage.setItem('sidebar_collapsed', isCollapsed.toString());
  }, [defaultCollapsed, isCollapsed]);

  useEffect(() => {
    if (defaultCollapsed === undefined) {
      lastDefaultCollapsedRef.current = undefined;
      return;
    }

    if (lastDefaultCollapsedRef.current !== defaultCollapsed) {
      lastDefaultCollapsedRef.current = defaultCollapsed;
      setIsCollapsed(defaultCollapsed);
    }
  }, [defaultCollapsed]);

  // Hover popout state
  const [hoveredItem, setHoveredItem] = useState(null);
  const [popoutPosition, setPopoutPosition] = useState({ top: 0 });
  const hoverTimeoutRef = useRef(null);

  // Helper to check if a path matches (including query params)
  const matchesPath = useCallback(
    (childPath) => {
      if (!childPath) return false;

      // Check if path contains query parameters
      if (childPath.includes('?')) {
        const [childPathname, childQuery] = childPath.split('?');
        const childParams = new URLSearchParams(childQuery);

        // Pathname must match
        if (pathname !== childPathname) return false;

        // All child query params must match current query params
        for (const [key, value] of childParams.entries()) {
          if (searchParams?.get(key) !== value) return false;
        }
        return true;
      }

      // Regular path matching (no query params)
      return pathname === childPath || pathname?.startsWith(`${childPath}/`);
    },
    [pathname, searchParams]
  );

  // Active section detection (for inline expansion)
  const getActiveSection = useCallback(() => {
    for (const item of menuItems) {
      if (item.children) {
        // Check if on landing page OR any child is active
        const isOnLandingPage = matchesPath(item.path);
        const hasActiveChild = item.children.some((child) => matchesPath(child.path));
        if (isOnLandingPage || hasActiveChild) return item.key;
      }
    }
    return null;
  }, [matchesPath]);

  const [activeSection, setActiveSection] = useState(getActiveSection());

  // Landing page detection (for special ON state styling)
  const isOnLandingPage = useCallback(
    (item) => {
      // Parent item with children: check if current path matches parent's landing page path exactly
      if (item.children && item.path) {
        return matchesPath(item.path);
      }
      return false;
    },
    [matchesPath]
  );

  // Update active section when pathname or search params change
  useEffect(() => {
    setActiveSection(getActiveSection());
  }, [pathname, searchParams, getActiveSection]);

  // User dropdown menu (Phase 9.4: Modernized - account actions only)
  const userMenuItems = useMemo(
    () => ({
      items: [
        // Non-clickable header showing user identity
        {
          key: 'user-identity',
          label: (
            <div style={{ padding: '4px 0', opacity: 0.7, fontSize: '12px' }}>
              Signed in as
              <div style={{ fontWeight: 600, opacity: 1, marginTop: '2px', fontSize: '14px' }}>
                {session?.user?.email}
              </div>
            </div>
          ),
          disabled: true, // Non-clickable
        },
        {
          type: 'divider',
        },
        {
          key: 'my-account',
          label: <Link href="/account/settings/profile">My Account</Link>,
          icon: <Icon name="UserOutlined" />,
        },
        {
          key: 'billing',
          label: <Link href="/account/billing/transactions">Billing</Link>,
          icon: <Icon name="CreditCardOutlined" />,
        },
        {
          type: 'divider',
        },
        {
          key: 'logout',
          label: 'Sign Out',
          icon: <Icon name="LogoutOutlined" />,
          onClick: () => signOut({ callbackUrl: '/auth/signin' }),
        },
      ],
    }),
    [session?.user?.email]
  );

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Handle cart click
  const handleCartClick = (e) => {
    e.preventDefault();
    setShowCart((prev) => !prev);
  };

  // Check if current path matches menu item path (uses matchesPath for consistency)
  const isActive = (itemPath) => {
    return matchesPath(itemPath);
  };

  // Handle hover on parent menu items
  const handleItemHover = (itemKey, element) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Calculate position for popout
    if (element) {
      const rect = element.getBoundingClientRect();
      setPopoutPosition({ top: rect.top });
    }

    setHoveredItem(itemKey);
  };

  // Handle mouse leave with delay
  const handleItemLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
    }, 150); // Small delay to allow moving to popout
  };

  // Toggle mobile submenu (tap to expand)
  const toggleMobileSubmenu = (key) => {
    setMobileOpenSubmenus((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Touch handlers for swipe to dismiss (Phase 9.3)
  const handleTouchStart = (e) => {
    if (!isMobile) return;

    // Only start drag if scrolled to top OR if dragging the handle
    const isAtTop = sheetRef.current?.scrollTop === 0;
    const isDragHandle = e.target.closest('[data-drag-handle]');

    console.log('[Touch] Start:', { isAtTop, isDragHandle: !!isDragHandle });

    if (isAtTop || isDragHandle) {
      setTouchStart(e.touches[0].clientY);
      setIsDragging(true);
      console.log('[Touch] Drag started at Y:', e.touches[0].clientY);
    }
  };

  const handleTouchMove = (e) => {
    if (!isMobile || !isDragging || !touchStart) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStart;

    // Only allow downward drags (positive diff) and prevent scroll bounce
    if (diff > 0) {
      e.preventDefault(); // Prevent scroll during drag
      setTouchCurrent(diff);
      // Apply top position change to sheet (starting from 35vh)
      if (sheetRef.current) {
        const viewportHeight = window.innerHeight;
        const startPosition = viewportHeight * 0.35; // 35vh in pixels
        const newTop = startPosition + diff;
        sheetRef.current.style.top = `${newTop}px`;
        console.log('[Touch] Moving:', diff, 'px', 'new top:', newTop);
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile || !isDragging) return;

    const threshold = window.innerHeight * 0.2; // 20% of screen height

    console.log('[Touch] End:', {
      touchCurrent,
      threshold,
      willDismiss: touchCurrent && touchCurrent > threshold,
    });

    if (touchCurrent && touchCurrent > threshold) {
      // Dismiss menu
      console.log('[Touch] Dismissing menu');
      setMobileMenuOpen(false);
    }

    // Reset to CSS-controlled position
    if (sheetRef.current) {
      sheetRef.current.style.top = '';
    }
    setTouchStart(null);
    setTouchCurrent(null);
    setIsDragging(false);
  };

  // Render navigation item with conditional inline/popout support
  const renderNavItem = (item) => {
    // CRITICAL: Parent items with children should NOT check their own path for active state
    // Only check if they're the active SECTION (one of their children is active)
    const active = item.children ? false : isActive(item.path);
    const isExternal = item.target === '_blank' || item.path?.startsWith('http');
    const navItemRef = useRef(null);

    // If item has children (submenu)
    if (item.children) {
      const isActiveSection = activeSection === item.key;
      const isHovered = hoveredItem === item.key;
      const isMobileSubmenuOpen = mobileOpenSubmenus.includes(item.key);

      // MOBILE BEHAVIOR: Tap to expand, always inline + clickable parent
      if (isMobile) {
        const isLandingPage = isOnLandingPage(item);
        const hasLandingPage = Boolean(item.path); // Check if parent has a landing page path

        const mobileParentContent = (
          <div
            className={`${styles.navItemWithChildren} ${active ? styles.active : ''} ${
              isMobileSubmenuOpen ? styles.expanded : ''
            } ${isLandingPage ? styles.landingPageActive : ''}`}
            onClick={(e) => {
              if (hasLandingPage) {
                e.preventDefault(); // Prevent navigation on click only if has landing page
              }
              toggleMobileSubmenu(item.key); // Expand/collapse submenu
            }}
          >
            {item.icon && (
              <span className={styles.navIcon}>
                <Icon name={item.icon} size={18} />
              </span>
            )}
            <span>{item.label}</span>
            <Icon
              name={isMobileSubmenuOpen ? 'DownOutlined' : 'RightOutlined'}
              size={16}
              className={styles.chevron}
            />
          </div>
        );

        return (
          <div key={item.key} className={styles.navSection}>
            {/* Parent item - Wrap in Link only if it has a landing page */}
            {hasLandingPage ? (
              <Link href={item.path} style={{ textDecoration: 'none' }}>
                {mobileParentContent}
              </Link>
            ) : (
              mobileParentContent
            )}

            {/* Mobile inline submenu (shown when tapped open) */}
            {isMobileSubmenuOpen && (
              <div className={styles.inlineSubmenu}>
                {item.children.map((child) => {
                  const childActive = isActive(child.path);
                  const childExternal = child.target === '_blank' || child.path?.startsWith('http');
                  return (
                    <Link
                      key={child.key}
                      href={child.path}
                      className={`${styles.inlineSubmenuItem} ${
                        childActive ? styles.activeWithIndicator : ''
                      }`}
                      target={childExternal ? '_blank' : undefined}
                      rel={childExternal ? 'noopener noreferrer' : undefined}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span>{child.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      }

      // DESKTOP BEHAVIOR: Hover popouts + active inline + clickable parent
      const isLandingPage = isOnLandingPage(item);
      const hasLandingPage = Boolean(item.path); // Check if parent has a landing page path

      const parentContent = (
        <div
          ref={navItemRef}
          className={`${styles.navItemWithChildren} ${active ? styles.active : ''} ${
            isActiveSection ? styles.expanded : ''
          } ${isLandingPage ? styles.landingPageActive : ''}`}
          onMouseEnter={(e) => handleItemHover(item.key, e.currentTarget)}
          onMouseLeave={handleItemLeave}
        >
          {item.icon && (
            <span className={styles.navIcon}>
              <Icon name={item.icon} size={24} />
            </span>
          )}
          {!isCollapsed && <span>{item.label}</span>}
          {!isCollapsed && (
            <Icon
              name={isActiveSection ? 'DownOutlined' : 'RightOutlined'}
              size={20}
              className={styles.chevron}
            />
          )}
        </div>
      );

      return (
        <div key={item.key} className={styles.navSection}>
          {/* Parent item - Clickable only if it has a landing page path */}
          {hasLandingPage ? (
            <Link href={item.path} style={{ textDecoration: 'none' }}>
              {parentContent}
            </Link>
          ) : (
            parentContent
          )}

          {/* Inline submenu (only if active section AND not collapsed) */}
          {isActiveSection && !isCollapsed && (
            <div className={styles.inlineSubmenu}>
              {item.children.map((child) => {
                const childActive = isActive(child.path);
                const childExternal = child.target === '_blank' || child.path?.startsWith('http');
                return (
                  <Link
                    key={child.key}
                    href={child.path}
                    className={`${styles.inlineSubmenuItem} ${
                      childActive ? styles.activeWithIndicator : ''
                    }`}
                    target={childExternal ? '_blank' : undefined}
                    rel={childExternal ? 'noopener noreferrer' : undefined}
                  >
                    <span>{child.label}</span>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Hover Popout Submenu (show if NOT active section OR collapsed) */}
          {(!isActiveSection || isCollapsed) && isHovered && (
            <div
              className={styles.popoutSubmenu}
              style={{ top: `${popoutPosition.top}px` }}
              onMouseEnter={() => {
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                }
                setHoveredItem(item.key);
              }}
              onMouseLeave={handleItemLeave}
            >
              {item.children.map((child) => {
                const childActive = isActive(child.path);
                const childExternal = child.target === '_blank' || child.path?.startsWith('http');
                return (
                  <Link
                    key={child.key}
                    href={child.path}
                    className={`${styles.popoutMenuItem} ${childActive ? styles.active : ''}`}
                    target={childExternal ? '_blank' : undefined}
                    rel={childExternal ? 'noopener noreferrer' : undefined}
                  >
                    <span>{child.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Regular nav item with link (no children)
    return (
      <Link
        key={item.key}
        href={item.path}
        className={`${styles.navItem} ${active ? styles.active : ''}`}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
      >
        {item.icon && (
          <span className={styles.navIcon}>
            <Icon name={item.icon} size={24} />
          </span>
        )}
        {!isCollapsed && <span>{item.label}</span>}
      </Link>
    );
  };

  return (
    <aside
      ref={sheetRef}
      className={`${styles.sidebar} ${!isMobile && isCollapsed ? styles.collapsed : ''} ${
        mobileMenuOpen ? styles.mobileOpen : ''
      }`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-mobile-sheet
      suppressHydrationWarning
    >
      {/* Drag Handle (Phase 9.2: Bottom Sheet - Mobile Only) */}
      {isMobile && (
        <div className={styles.dragHandle} data-drag-handle>
          <span className={styles.dragHandleLine} />
        </div>
      )}

      {/* Logo - Memoized to prevent re-render on hover */}
      <SidebarLogo isCollapsed={isCollapsed} />

      {/* Divider after logo */}
      <div className={styles.logoDivider} />

      {/* Navigation */}
      <nav className={styles.nav}>{menuItems.map((item) => renderNavItem(item))}</nav>

      {/* Collapse Button - Icon-Only Control (hidden on mobile) */}
      {!isMobile && (
        <Tooltip
          title={isCollapsed ? 'Expand menu' : 'Collapse menu'}
          placement="right"
          mouseEnterDelay={0.3}
        >
          <button
            className={`${styles.collapseButton} ${isCollapsed ? styles.collapsedState : ''}`}
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? 'Expand menu' : 'Collapse menu'}
          >
            <Icon
              name={isCollapsed ? 'LayoutSidebarLeftExpand' : 'LayoutSidebarLeftCollapse'}
              size={26}
              stroke={1}
            />
          </button>
        </Tooltip>
      )}

      {/* User Section */}
      {session && (
        <div className={styles.userSection}>
          <Dropdown menu={userMenuItems} placement="topRight" trigger={['click']}>
            <div className={styles.userInfo}>
              <Avatar
                size={40}
                style={{ backgroundColor: '#05c168' }}
                className={styles.userAvatar}
              >
                {getUserInitials(session?.user?.name)}
              </Avatar>
              {!isCollapsed && (
                <div className={styles.userDetails}>
                  <div className={styles.userName}>{session?.user?.name || 'User'}</div>
                  <div className={styles.userEmail}>{session?.user?.email || ''}</div>
                </div>
              )}
            </div>
          </Dropdown>
        </div>
      )}
      {!session && !isCollapsed && <div className={styles.userSection}>Loading user...</div>}
    </aside>
  );
};

AccountSidebar.propTypes = {
  session: PropTypes.shape({
    user: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
      image: PropTypes.string,
    }),
  }),
  isMobile: PropTypes.bool,
  mobileMenuOpen: PropTypes.bool,
  setMobileMenuOpen: PropTypes.func,
  defaultCollapsed: PropTypes.bool,
};

export default AccountSidebar;
